import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from "react";
import {
  Archive,
  Building2,
  ClipboardPen,
  CornerDownLeft,
  FileText,
  LayoutTemplate,
  Package,
  Plus,
  Search,
  Megaphone,
  Send,
  Settings,
  Sparkles,
} from "lucide-react";
import { Dialog } from "../../components/Dialog";
import { api } from "../../api/client";
import { SYSTEM_SENT_URL } from "../../api/mailbox";
import { canSeeNav } from "../../layout/navRoles";
import { useAuth } from "../auth/useAuth";
import { formatMoney } from "../../lib/money";
import { commandPaletteHint, isCommandPaletteShortcut, useGlobalKeydown, wrapIndex } from "../../lib/keyboard";
import type { CatalogHit, GlobalSearchResult, QuoteSummary, TemplateSummary } from "../../api/types";

// ⌘K / Ctrl+K 全域搜尋。單一輸入框跨四種目標:
// 報價單(開啟)、品項庫(帶入目前章節)、範本(套用)、本單明細行(捲到該行)。
// 沒打字時列出常用動作,讓面板同時是「跳去哪」的捷徑。
//
// 已封存的報價單**不列在結果裡**(後端就排除掉了,規格 §6),但結果底下會多一行
// 「封存區裡有 N 張符合的單」。少了那一行,打單號查無此單會被當成系統把資料弄丟了;
// 而把那 N 張補進結果清單同樣不行 —— 那個數字是提示,不是結果。

/** 打幾個字才送出搜尋。與行內品項建議一致,避免一個字就打爆後端。 */
const MIN_QUERY_LENGTH = 2;
/** 送出前的等待時間,邊打邊搜但不是每個字都送。 */
const SEARCH_DEBOUNCE_MS = 300;

export interface CommandPaletteController {
  open: boolean;
  openPalette: () => void;
  closePalette: () => void;
  toggle: () => void;
}

/**
 * 面板的開關狀態與快捷鍵。⌘K / Ctrl+K 開啟或關閉,Esc 關閉。
 * enabled 為 false 時只保留狀態不掛快捷鍵,避免外框與面板各掛一次而互相抵銷。
 */
export function useCommandPalette(enabled = true): CommandPaletteController {
  const [open, setOpen] = useState(false);

  const openPalette = useCallback(() => setOpen(true), []);
  const closePalette = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((prev) => !prev), []);

  useGlobalKeydown((event) => {
    if (isCommandPaletteShortcut(event)) {
      // 瀏覽器自己的 ⌘K(網址列搜尋)要讓開。
      event.preventDefault();
      toggle();
      return;
    }
    if (event.key === "Escape") closePalette();
  }, enabled);

  return { open, openPalette, closePalette, toggle };
}

/**
 * 後端 `GET /api/search` 另外回的「封存區裡有幾張符合」(規格 §6.1)。
 *
 * `api/types.ts` 的 `GlobalSearchResult` 還沒補上這一欄,那份型別檔不在這個
 * Task 的範圍內 —— 在這裡就地補宣告,總好過讓它變成一個沒有型別的 `any`。
 */
interface SearchResultWithArchive extends GlobalSearchResult {
  archived_hits?: number;
}

/** 本單明細行的跳轉目標,由編輯器提供。 */
export interface CommandLineTarget {
  id: number | string;
  /** 明細摘要。 */
  name: string;
  /** 所屬章節標題,列在第二行讓人分辨同名項目。 */
  sectionTitle: string;
}

type CommandGroup = "action" | "quote" | "catalog" | "template" | "line";

interface CommandItem {
  key: string;
  group: CommandGroup;
  icon: ReactNode;
  title: string;
  subtitle?: string;
  /** 右側附註,例如金額。 */
  meta?: string;
  run: () => void;
}

const GROUP_LABEL: Record<CommandGroup, string> = {
  action: "動作",
  quote: "報價單",
  catalog: "品項庫",
  template: "範本",
  line: "本單明細",
};

const GROUP_ORDER: CommandGroup[] = ["action", "quote", "line", "catalog", "template"];

export interface CommandPaletteProps {
  /** 導頁。外框會傳 React Router 的 navigate;沒有時退回整頁跳轉。 */
  onNavigate?: (path: string) => void;
  /** 選到品項庫項目:帶入目前游標所在章節。 */
  onPickCatalog?: (hit: CatalogHit) => void;
  /** 選到範本:套用到目前這單。 */
  onApplyTemplate?: (template: TemplateSummary) => void;
  /** 本單明細行,供就地搜尋與跳轉。 */
  lines?: CommandLineTarget[];
  /** 選到本單明細行:捲動並聚焦該行。 */
  onJumpLine?: (id: number | string) => void;
  /** 與外框共用的開關。傳入時面板不再自行掛快捷鍵。 */
  controller?: CommandPaletteController;
}

export function CommandPalette({
  onNavigate,
  onPickCatalog,
  onApplyTemplate,
  lines = [],
  onJumpLine,
  controller,
}: CommandPaletteProps) {
  // 誰在按這個面板。沒有 AuthProvider 時是 null(單獨 render 的測試),
  // 那時一項都不濾 —— 與側邊欄同一條規矩,見 `canSeeNav`。
  const { user } = useAuth();

  // 外部沒給 controller 時自己管狀態,元件才能單獨使用與測試。
  const fallback = useCommandPalette(controller == null);
  const ctl = controller ?? fallback;
  const { open, closePalette } = ctl;

  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [remote, setRemote] = useState<SearchResultWithArchive | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const navigate = useCallback(
    (path: string) => {
      if (onNavigate) {
        onNavigate(path);
        return;
      }
      // 沒接上路由時退回整頁跳轉,前端掛在 /quote/ 底下。
      if (typeof window !== "undefined") window.location.assign(`/quote${path}`);
    },
    [onNavigate],
  );

  // 每次關閉都清空,下次打開是乾淨的輸入框。
  useEffect(() => {
    if (open) return;
    setQuery("");
    setActiveIndex(0);
    setRemote(null);
    setLoading(false);
    setFailed(false);
  }, [open]);

  // 打開後把焦點放進輸入框。
  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
  }, [open]);

  const trimmed = query.trim();

  // 搜尋:debounce 後打 /api/search。字數不足或面板沒開就不送。
  useEffect(() => {
    if (!open || trimmed.length < MIN_QUERY_LENGTH) {
      setRemote(null);
      setLoading(false);
      setFailed(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const timer = window.setTimeout(() => {
      api
        .get<SearchResultWithArchive>("/search", { q: trimmed })
        .then((result) => {
          if (cancelled) return;
          setRemote(result);
          setFailed(false);
        })
        .catch(() => {
          if (cancelled) return;
          setRemote(null);
          setFailed(true);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, trimmed]);

  // 沒打字時列出來的那幾個捷徑。**按角色過濾,用的是側邊欄那一份對照表**
  // (`layout/navRoles.ts`):⌘K 是這個系統的第二個全域導覽面,只過濾側邊欄的話,
  // 財務按 ⌘K 照樣點得進品項庫 —— 進去是一頁載不出來的品項庫(後端 403),
  // 而「新增報價單」是填完整張才收到 403。兩邊答案不一致比兩邊都不過濾更難查。
  const actionItems = useMemo<CommandItem[]>(() => {
    const go = (path: string) => () => {
      closePalette();
      navigate(path);
    };
    const raw = [
      { key: "action-new", path: "/quotes/new", title: "新增報價單", icon: <Plus aria-hidden="true" size={16} /> },
      { key: "action-quotes", path: "/quotes", title: "報價單", icon: <FileText aria-hidden="true" size={16} /> },
      {
        key: "action-templates",
        path: "/templates",
        title: "範本",
        icon: <LayoutTemplate aria-hidden="true" size={16} />,
      },
      { key: "action-catalog", path: "/catalog", title: "品項庫", icon: <Package aria-hidden="true" size={16} /> },
      {
        key: "action-companies",
        path: "/companies",
        title: "客戶",
        icon: <Building2 aria-hidden="true" size={16} />,
      },
      { key: "action-workorders", path: "/workorders", title: "製單", icon: <ClipboardPen aria-hidden="true" size={16} /> },
      { key: "action-system-sent", path: SYSTEM_SENT_URL, title: "系統寄出", icon: <Send aria-hidden="true" size={16} /> },
      { key: "action-bulletin", path: "/bulletin", title: "公佈欄", icon: <Megaphone aria-hidden="true" size={16} /> },
      { key: "action-settings", path: "/settings", title: "設定", icon: <Settings aria-hidden="true" size={16} /> },
      { key: "action-updates", path: "/updates", title: "更新紀錄", icon: <Sparkles aria-hidden="true" size={16} /> },
    ];
    return raw
      .filter((item) => canSeeNav(item.path, user?.role))
      .map(({ path, ...item }) => ({ ...item, group: "action" as const, run: go(path) }));
  }, [closePalette, navigate, user?.role]);

  const items = useMemo<CommandItem[]>(() => {
    const keyword = trimmed.toLocaleLowerCase();
    const matchedActions =
      keyword === "" ? actionItems : actionItems.filter((item) => item.title.toLocaleLowerCase().includes(keyword));

    const lineItems: CommandItem[] =
      keyword === ""
        ? []
        : lines
            .filter((line) => `${line.name} ${line.sectionTitle}`.toLocaleLowerCase().includes(keyword))
            .slice(0, 8)
            .map((line) => ({
              key: `line-${line.id}`,
              group: "line" as const,
              icon: <CornerDownLeft aria-hidden="true" size={16} />,
              title: line.name || "(未命名明細)",
              subtitle: line.sectionTitle,
              run: () => {
                closePalette();
                onJumpLine?.(line.id);
              },
            }));

    const quoteItems: CommandItem[] = (remote?.quotes ?? []).map((quote: QuoteSummary) => ({
      key: `quote-${quote.id}`,
      group: "quote" as const,
      icon: <FileText aria-hidden="true" size={16} />,
      title: quote.title || quote.quote_no,
      subtitle: `${quote.quote_no} · ${quote.client_name}`,
      meta: formatMoney(quote.total),
      run: () => {
        closePalette();
        navigate(`/quotes/${quote.id}`);
      },
    }));

    const catalogItems: CommandItem[] = (remote?.catalog ?? []).map((hit: CatalogHit) => ({
      key: `catalog-${hit.variant_id}`,
      group: "catalog" as const,
      icon: <Package aria-hidden="true" size={16} />,
      title: hit.item_name,
      subtitle: [hit.spec, hit.category].filter(Boolean).join(" · "),
      meta: formatMoney(hit.price),
      run: () => {
        closePalette();
        if (onPickCatalog) onPickCatalog(hit);
        else navigate("/catalog");
      },
    }));

    const templateItems: CommandItem[] = (remote?.templates ?? []).map((template: TemplateSummary) => ({
      key: `template-${template.id}`,
      group: "template" as const,
      icon: <LayoutTemplate aria-hidden="true" size={16} />,
      title: template.name,
      subtitle: template.description,
      run: () => {
        closePalette();
        if (onApplyTemplate) onApplyTemplate(template);
        else navigate("/templates");
      },
    }));

    const all = [...matchedActions, ...quoteItems, ...lineItems, ...catalogItems, ...templateItems];
    // 依固定的群組順序排,結果才不會每打一個字就整份跳位。
    return GROUP_ORDER.flatMap((group) => all.filter((item) => item.group === group));
  }, [actionItems, closePalette, lines, navigate, onApplyTemplate, onJumpLine, onPickCatalog, remote, trimmed]);

  // 結果換了就把游標拉回第一筆。
  useEffect(() => {
    setActiveIndex(0);
  }, [items.length]);

  // 讓選取中的項目保持在可視範圍;jsdom 沒有 scrollIntoView,先確認再呼叫。
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const el = list.querySelector<HTMLElement>('[data-active="true"]');
    if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "nearest" });
  }, [activeIndex, items.length]);

  const onInputKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((prev) => wrapIndex(prev, items.length, 1));
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((prev) => wrapIndex(prev, items.length, -1));
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      items[activeIndex]?.run();
    }
  };

  const listboxId = "nq-command-listbox";
  const activeItem = items[activeIndex];

  // 封存區裡有幾張符合。後端沒回(舊版本、或這次搜尋失敗)就當成 0,不顯示那一行。
  const archivedHits = remote?.archived_hits ?? 0;

  /** 帶著同一個關鍵字去封存區 —— 使用者不必到了那邊再打一次單號。 */
  const goToArchive = () => {
    closePalette();
    navigate(`/quotes/archive?q=${encodeURIComponent(trimmed)}`);
  };

  let statusText = "";
  if (loading) statusText = "搜尋中…";
  else if (failed) statusText = "搜尋失敗,請稍後再試";
  else if (trimmed !== "" && trimmed.length < MIN_QUERY_LENGTH)
    statusText = `再多打 ${MIN_QUERY_LENGTH - trimmed.length} 個字開始搜尋`;
  else if (items.length === 0) statusText = "沒有符合的結果";

  let lastGroup: CommandGroup | null = null;

  return (
    <Dialog
      open={open}
      onClose={closePalette}
      label="全域指令面板"
      showClose={false}
      maxWidth="max-w-xl"
      contentClassName="p-0"
    >
      <div className="flex items-center gap-2 border-b border-[var(--border)] px-3 py-2.5 focus-within:border-[var(--accent)]">
        <Search aria-hidden="true" size={18} className="shrink-0 text-[var(--text-3)]" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onInputKeyDown}
          aria-label="搜尋報價單、品項庫、範本或本單明細"
          role="combobox"
          aria-expanded="true"
          aria-controls={listboxId}
          aria-activedescendant={activeItem ? `${listboxId}-${activeItem.key}` : undefined}
          aria-autocomplete="list"
          autoComplete="off"
          placeholder="輸入關鍵字,或直接選下方動作"
          // 不要加 outline-none:它是 outline: 2px solid transparent,
          // 會蓋掉 tokens.css 的 :focus-visible,聚焦時就完全沒有可見指示。
          className="h-8 w-full min-w-0 border-0 bg-transparent text-sm text-[var(--text)] placeholder:text-[var(--text-3)]"
        />
        <kbd className="shrink-0 rounded border border-[var(--border-strong)] px-1.5 py-0.5 text-xs text-[var(--text-3)]">
          Esc
        </kbd>
      </div>

      <ul
        ref={listRef}
        id={listboxId}
        role="listbox"
        aria-label="結果清單"
        className="m-0 max-h-[min(24rem,55vh)] list-none overflow-y-auto p-1"
      >
        {items.map((item, index) => {
          const active = index === activeIndex;
          const showHeading = item.group !== lastGroup;
          lastGroup = item.group;
          return (
            <li key={item.key} role="presentation" className="m-0">
              {showHeading ? (
                <p
                  role="presentation"
                  className="m-0 px-2 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-[var(--text-3)]"
                >
                  {GROUP_LABEL[item.group]}
                </p>
              ) : null}
              <div
                id={`${listboxId}-${item.key}`}
                role="option"
                aria-selected={active}
                data-active={active ? "true" : "false"}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={item.run}
                className={
                  "flex cursor-pointer items-center gap-2.5 rounded-[var(--radius)] px-2 py-1.5 text-sm " +
                  (active ? "bg-[var(--accent-soft)] text-[var(--accent-hover)]" : "text-[var(--text)]")
                }
              >
                <span className="shrink-0 text-[var(--text-3)]">{item.icon}</span>
                <span className="min-w-0 flex-1">
                  <span className="block break-words">{item.title}</span>
                  {item.subtitle ? (
                    <span className="block break-words text-xs text-[var(--text-3)]">{item.subtitle}</span>
                  ) : null}
                </span>
                {item.meta ? <span className="tabular shrink-0 text-xs text-[var(--text-2)]">{item.meta}</span> : null}
              </div>
            </li>
          );
        })}
      </ul>

      {statusText ? (
        <p role="status" className="m-0 border-t border-[var(--border)] px-3 py-2 text-sm text-[var(--text-3)]">
          {statusText}
        </p>
      ) : null}

      {/* 安靜的一行提示(規格 §6.1)。**在結果清單外面**,不混進 listbox ——
          它不是一筆結果,上下鍵也不該停在它上面;但它是一顆真的按鈕,
          Tab 走得到、Enter 按得下去。 */}
      {archivedHits > 0 ? (
        <button
          type="button"
          onClick={goToArchive}
          className="flex w-full items-center gap-2 border-t border-[var(--border)] px-3 py-2 text-left text-sm text-[var(--text-2)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]"
        >
          <Archive aria-hidden="true" size={16} className="shrink-0 text-[var(--text-3)]" />
          <span>封存區裡有 {archivedHits} 張符合的單</span>
        </button>
      ) : null}

      <p className="m-0 flex items-center justify-between gap-2 border-t border-[var(--border)] px-3 py-2 text-xs text-[var(--text-3)]">
        <span>↑↓ 選擇 · Enter 執行 · Esc 關閉</span>
        <span>{commandPaletteHint()}</span>
      </p>
    </Dialog>
  );
}
